import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import inventoryService, {
  Item,
  ItemCategory,
  ItemWithStock,
  StockMovementRow,
  UomUnit,
} from '../services/inventory.service';
import warehouseService, { CreateWarehouseInput, UpdateWarehouseInput, Warehouse } from '../services/warehouse.service';

export const useItems = (
  filters?: {
    category_id?: string;
    item_type?: string;
    is_active?: boolean;
    search?: string;
    /** Cap rows for pickers (avoids full-catalog timeouts). */
    limit?: number;
  },
  options?: { enabled?: boolean }
): UseQueryResult<Item[], Error> => {
  return useQuery({
    queryKey: ['items', filters],
    queryFn: () => inventoryService.getAllItems(filters),
    enabled: options?.enabled ?? true,
  });
};

export const useItem = (id: string, options?: { enabled?: boolean }): UseQueryResult<Item, Error> => {
  return useQuery({
    queryKey: ['items', id],
    queryFn: () => inventoryService.getItemById(id),
    enabled: options?.enabled !== false && !!id,
  });
};

export const useItemsWithStock = (
  warehouseId?: string,
  queryOptions?: { enabled?: boolean }
): UseQueryResult<ItemWithStock[], Error> => {
  return useQuery({
    queryKey: ['items', 'stock', warehouseId],
    queryFn: () => inventoryService.getItemsWithStock(warehouseId),
    enabled: queryOptions?.enabled ?? true,
  });
};

export const useStockMovements = (
  filters?: {
    warehouse_id?: string;
    transaction_type?: string;
    from_date?: string;
    to_date?: string;
    search?: string;
    limit?: number;
  },
  queryOptions?: { enabled?: boolean }
): UseQueryResult<StockMovementRow[], Error> => {
  return useQuery({
    queryKey: ['stock-movements', filters],
    queryFn: () => inventoryService.getStockMovements(filters),
    enabled: queryOptions?.enabled ?? true,
  });
};

export const useLowStockItems = (): UseQueryResult<ItemWithStock[], Error> => {
  return useQuery({
    queryKey: ['items', 'low-stock'],
    queryFn: () => inventoryService.getLowStockItems(),
  });
};

export const useCreateItem = (): UseMutationResult<Item, Error, Partial<Item>> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => inventoryService.createItem(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      message.success('Item created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create item');
    },
  });
};

export const useQuickCreateItem = (): UseMutationResult<
  { item: Item; stock_posted: boolean },
  Error,
  {
    item_name: string;
    base_uom: string;
    item_code?: string;
    standard_cost?: number;
    item_type?: string;
    is_critical?: boolean;
    heat_number_mandatory?: boolean;
    mtc_required_on_receipt?: boolean;
    initial_stock?: {
      warehouse_id: string;
      quantity: number;
      unit_rate?: number;
      heat_number?: string;
      dimension?: string;
    };
  }
> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => inventoryService.quickCreateItem(data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      message.success(
        result.stock_posted
          ? 'Item created with opening / initial stock'
          : 'Item created successfully'
      );
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create item');
    },
  });
};

export const useUpdateItem = (): UseMutationResult<Item, Error, { id: string; data: Partial<Item> }> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }) => inventoryService.updateItem(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['items', data.id] });
      message.success('Item updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update item');
    },
  });
};

export const useDeleteItem = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => inventoryService.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      message.success('Item deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete item');
    },
  });
};

export const useWarehouses = () => {
  return useQuery({
    queryKey: ['warehouses'],
    queryFn: () => warehouseService.getAll(),
  });
};

export const useWarehouse = (id: string | null): UseQueryResult<Warehouse, Error> => {
  return useQuery({
    queryKey: ['warehouses', id],
    queryFn: () => warehouseService.getById(id!),
    enabled: !!id,
  });
};

export const useCreateWarehouse = (): UseMutationResult<Warehouse, Error, CreateWarehouseInput> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => warehouseService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      message.success('Warehouse created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create warehouse');
    },
  });
};

export const useUpdateWarehouse = (): UseMutationResult<Warehouse, Error, { id: string; data: UpdateWarehouseInput }> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: ({ id, data }) => warehouseService.update(id, data),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      queryClient.invalidateQueries({ queryKey: ['warehouses', data.id] });
      message.success('Warehouse updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update warehouse');
    },
  });
};

export const useDeleteWarehouse = (): UseMutationResult<void, Error, string> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (id) => warehouseService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['warehouses'] });
      message.success('Warehouse deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete warehouse');
    },
  });
};

export const useItemCategories = (): UseQueryResult<ItemCategory[], Error> => {
  return useQuery({
    queryKey: ['item-categories'],
    queryFn: () => inventoryService.getAllCategories(),
  });
};

export const useItemCategoryTree = (): UseQueryResult<ItemCategory[], Error> => {
  return useQuery({
    queryKey: ['item-categories', 'tree'],
    queryFn: () => inventoryService.getCategoryTree(),
  });
};

export const useUomUnits = (options?: {
  all?: boolean;
  enabled?: boolean;
}): UseQueryResult<UomUnit[], Error> => {
  return useQuery({
    queryKey: ['inventory', 'uom-units', options?.all === true ? 'all' : 'active'],
    queryFn: () => inventoryService.getUomUnits({ all: options?.all }),
    enabled: options?.enabled !== false,
  });
};

export const useCreateItemCategory = (): UseMutationResult<ItemCategory, Error, Partial<ItemCategory>> => {
  const queryClient = useQueryClient();
  const { message } = App.useApp();

  return useMutation({
    mutationFn: (data) => inventoryService.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item-categories'] });
      message.success('Category created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create category');
    },
  });
};
